// Firmware 0.3.7's timed wakes (`sched`), capture now (`do`) and reboot, as the
// node page edits them. The grammar, limits and plain words MIRROR the
// backend's padihat/rtct/schedule.py, which stays the authority: the settings
// endpoint refuses anything the node would refuse, this only says so first.
//
// sched: entries joined by ";", all times UTC.
//   HHMM[.DD]=TASKS  every day at HH:MM UTC; .DD = hex mask of the UTC weekdays
//                    (bit 0 Monday .. bit 6 Sunday)
//   @EPOCH=TASKS     once, at that UTC second (a satellite pass)
// TASKS: p photo pair, g GNSS fix, b radar burst, u upload, k ground-truth
// packet (= p g b u). At most 12 entries and 200 characters; two entries on a
// shared day closer than 5 minutes are refused.

export const SCHED_MAX = 12;
export const SCHED_TEXT_MAX = 200;
export const SCHED_MIN_GAP_S = 300;
const EPOCH_MIN = 1700000000;
const EPOCH_MAX = 4102444800;
export const TASK_LETTERS = "pgbuk";
export const TASK_NAMES = {
  p: "photo pair",
  g: "GNSS fix",
  b: "radar burst",
  u: "upload",
  k: "ground-truth packet",
};
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const ALL_DAYS = 0x7f;
const ID_RE = /^[A-Za-z0-9_-]{1,24}$/;

export class ScheduleError extends Error {}

export function parseTasks(text) {
  if (!text || [...text].some((c) => !TASK_LETTERS.includes(c)))
    throw new ScheduleError("tasks must be one or more of p, g, b, u, k");
  const tasks = new Set(text);
  if (tasks.has("k")) "pgbu".split("").forEach((c) => tasks.add(c));
  return tasks;
}

export function parseEntry(text) {
  const eq = text.indexOf("=");
  if (eq < 0) throw new ScheduleError("an entry is HHMM[.DD]=TASKS or @EPOCH=TASKS");
  const head = text.slice(0, eq);
  const tasks = parseTasks(text.slice(eq + 1));
  if (head.startsWith("@")) {
    const digits = head.slice(1);
    if (!/^\d{1,10}$/.test(digits) || +digits < EPOCH_MIN || +digits > EPOCH_MAX)
      throw new ScheduleError("a one-off entry is @ and a UTC epoch");
    return { at: +digits, tasks, text };
  }
  if (![4, 7].includes(head.length) || !/^\d{4}/.test(head))
    throw new ScheduleError("a daily entry starts with HHMM (UTC)");
  const hh = +head.slice(0, 2);
  const mm = +head.slice(2, 4);
  if (hh > 23 || mm > 59) throw new ScheduleError("a daily entry starts with HHMM (UTC)");
  let days = ALL_DAYS;
  if (head.length === 7) {
    if (head[4] !== "." || !/^[0-9a-fA-F]{2}$/.test(head.slice(5)))
      throw new ScheduleError("weekdays are .DD, two hex digits");
    days = parseInt(head.slice(5), 16);
    if (days < 1 || days > ALL_DAYS) throw new ScheduleError("weekdays are .01 to .7f");
  }
  return { min: hh * 60 + mm, days, tasks, text };
}

// The entries of a sched value; ScheduleError on anything the node would refuse.
export function parseSched(text) {
  if (typeof text !== "string") throw new ScheduleError("sched is text");
  if (text.length > SCHED_TEXT_MAX)
    throw new ScheduleError(`sched is at most ${SCHED_TEXT_MAX} characters`);
  if (text === "") return [];
  const parts = text.split(";");
  if (parts.some((p) => p === "")) throw new ScheduleError("an empty entry (a stray ;)");
  if (parts.length > SCHED_MAX) throw new ScheduleError(`at most ${SCHED_MAX} entries`);
  const entries = parts.map(parseEntry);
  for (let i = 0; i < entries.length; i += 1) {
    for (let j = i + 1; j < entries.length; j += 1) {
      const a = entries[i];
      const b = entries[j];
      if ("at" in a && "at" in b && Math.abs(a.at - b.at) < SCHED_MIN_GAP_S)
        throw new ScheduleError("two entries less than 5 minutes apart");
      if ("min" in a && "min" in b && a.days & b.days) {
        const d = Math.abs(a.min - b.min);
        if (Math.min(d, 1440 - d) * 60 < SCHED_MIN_GAP_S)
          throw new ScheduleError("two entries less than 5 minutes apart");
      }
    }
  }
  return entries;
}

export function schedProblem(text) {
  try {
    parseSched(text);
    return null;
  } catch (e) {
    return e instanceof ScheduleError ? e.message : "not a valid schedule";
  }
}

// Task letters in a fixed order; k alone stands for the whole packet.
export const tasksText = (tasks) =>
  tasks.has("k") ? "k" : TASK_LETTERS.split("").filter((c) => c !== "k" && tasks.has(c)).join("");

const pad = (n) => String(n).padStart(2, "0");

// A daily entry the person set in node-local time -> the UTC entry text. The
// weekdays move with the time when it crosses midnight in UTC.
export function dailyText({ localMinute, localDays = ALL_DAYS, tasks }, tzHours) {
  const total = localMinute - Math.round(tzHours * 60);
  const shift = Math.floor(total / 1440);
  const min = total - shift * 1440;
  let days = 0;
  for (let d = 0; d < 7; d += 1) if (localDays & (1 << d)) days |= 1 << (((d + shift) % 7) + 7) % 7;
  const head = `${pad(Math.floor(min / 60))}${pad(min % 60)}`;
  return `${head}${days === ALL_DAYS ? "" : `.${days.toString(16).padStart(2, "0")}`}=${tasksText(tasks)}`;
}

// A parsed daily entry -> what the person sees: node-local minute and days.
export function localDaily(entry, tzHours) {
  const total = entry.min + Math.round(tzHours * 60);
  const shift = Math.floor(total / 1440);
  let localDays = 0;
  for (let d = 0; d < 7; d += 1) if (entry.days & (1 << d)) localDays |= 1 << (((d + shift) % 7) + 7) % 7;
  return { localMinute: total - shift * 1440, localDays, tasks: entry.tasks };
}

function taskWords(tasks) {
  return "kpgbu"
    .split("")
    .filter((c) => tasks.has(c) && (c === "k" || !tasks.has("k")))
    .map((c) => TASK_NAMES[c])
    .join(", ");
}

// Plain words for each entry in node-local time, as the backend's describe().
export function describe(entries, tzHours = 8) {
  return entries.map((e) => {
    const tasks = taskWords(e.tasks);
    if ("at" in e) {
      // Fixed names, as Python's %a %b (a browser's locale may say "Sept").
      const t = new Date((e.at + tzHours * 3600) * 1000);
      const day = WEEKDAYS[(t.getUTCDay() + 6) % 7];
      const month = MONTHS[t.getUTCMonth()];
      return `once on ${day} ${pad(t.getUTCDate())} ${month} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:${pad(t.getUTCSeconds())}: ${tasks}`;
    }
    const local = localDaily(e, tzHours);
    const days = WEEKDAYS.filter((_, d) => local.localDays & (1 << d));
    const when = days.length === 7 ? "every day" : days.join(" ");
    return `${when} at ${pad(Math.floor(local.localMinute / 60))}:${pad(local.localMinute % 60)}: ${tasks}`;
  });
}

// A fresh request id for `do` / `reboot` (1-24 of [A-Za-z0-9_-]): the node
// ignores an id it has seen, so every press needs a new one.
let counter = 0;
export function freshId(prefix = "c", now = Date.now()) {
  counter = (counter + 1) % 1296;
  const id = `${prefix}${now.toString(36)}${counter.toString(36)}`.slice(0, 24);
  return ID_RE.test(id) ? id : `c${now.toString(36)}`;
}

export const doValue = (id, tasks) => `${id}:${tasksText(tasks)}`;
export const idValid = (id) => ID_RE.test(id);
